import _ from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ApplicableTableSettings, codesAirportList } from '../../../../tableSettings';

import useFastStorage from '../../../../hooks/useFastStorage';
import useFunction from '../../../../hooks/useFunction';
import useGlobalOkDialog from '../../../../hooks/useGlobalOkDialog';

import ApplicationContext from '../../../../ApplicationContext';
import { Search, initialSearch, isFilterObjectEmpty } from '../../../../filtering';
import { Portal } from 'react-portal';
import { CodeAirportListItem } from '../../../../fms.data/models/CodeAirportListItem';
import { JobNames } from '../../../../framework.core/JobNames';
import { ApiResult, InsertResult } from '../../../../framework.core/data/ApiResult';
import { SQLType } from '../../../../framework.core/data/SQLInfo';
import { FmsSecTask } from '../../../../fms.core/constants';
import TextBox from '../../../../ui/TextBox';
import EditableTable, { CellData, EditableTableHandle, TableCellAligns, TableCellTypes } from '../../../../ui/tables/editing/EditableTable';
import GreenButton from '../../../../ui/GreenButton';
import { checkTabIsDirty, sortingListData } from '../../ReferenceFiles';
import { findSecurityTask } from '../../../../util';
import useFilterBar from '../../../../hooks/useFilterBar';
import { ListOrderBy } from "../../../common/AbstractListView";

import BlackInverseButton from '../../../../ui/BlackInverseButton';
import constants from '../../../../constants';
import IFmsReferenceFilesContext from '../../IfmsReferenceFilesContext';
import CloseButton from '../../../../ui/CloseButton';
import useFunctionKeys from '../../../../hooks/useFunctionKeys';

class CodeAirport extends CodeAirportListItem {
  isNew: boolean = false;
  isDelete: boolean = false;
  isUpdate: boolean = false;
}

function PortNameRenderer({ data, row, cell }: any) {
  useEffect(() => {
    const focusElement = document.getElementById('ifms-airport-name') as HTMLInputElement;
    if (focusElement) {
      focusElement.focus();
      focusElement.select();
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const changeLineItem = data[0].changeLineItem;
  return (
      <TextBox maxLength={50} textId={`ifms-airport-name-${row.index}`} value={data[row.index].portName} onChange={(e) => {
        changeLineItem(row.index, { portName: e.target.value, isUpdate: true })
      }} />
  );

}

function CodeRenderer({ data, row, cell }: any) {
  const changeLineItem = data[0].changeLineItem;
  return (
      <TextBox maxLength={3} textId={`ifms-airport-code-${row.index}`} value={data[row.index].code} onChange={(e) => {
        changeLineItem(row.index, { code: e.target.value, isUpdate: true })
      }} />
  );
}

function CustomCodeRenderer({ data, row, cell }: any) {
  const changeLineItem = data[0].changeLineItem;
  return (
      <TextBox maxLength={10} textId={`ifms-airport-customcode-${row.index}`} value={data[row.index].usCode} onChange={(e) => {
        changeLineItem(row.index, { usCode: e.target.value, isUpdate: true })
      }} />
  );
}

class EditableCodeAirportInfoListItem extends CodeAirportListItem {
  isNew: boolean = false;
  isDelete: boolean = false;
  isUpdate: boolean = false;
}

function formatElement(data: any) {
  const result = new EditableCodeAirportInfoListItem();
  result.code = data.code;
  result.usCode = data.usCode;
  result.portName = data.portName;
  result.cid = data.cid;
  result.isNew = false;
  result.isDelete = false;

  return data;
}

function formatData(data: Array<any>) {
  return data.map(d => formatElement(d));
}

function Airports({
  componentIndex
}: {
  componentIndex: number
}) {
  useFunction({
    invokers: {
      ifmsReferenceFilesSave: save,
      ifmsReferenceFilesCancel: cancelOperation,
    }
  });
  useFunctionKeys({ onSave: handleSaveAndClose });

  const appContext = useContext(ApplicationContext);
  const refContext = useContext(IFmsReferenceFilesContext);
  const [showGlobalOkDialog] = useGlobalOkDialog();

  const savedDataRef = useRef<Array<CodeAirportListItem>>([]);
  const oriData = useRef<CodeAirportListItem[]>([]);
  const tableRef = useRef<EditableTableHandle>(null);
  const masterTableRef = useRef<EditableTableHandle>(null);

  const [loading, setLoading] = useState(false);
  const [allData, setAllData] = useState(Array<String>());
  //const addingToTheFirstLine = useRef(false);
  const [hasChanges, setHasChanges] = useFastStorage(`ifms-referenceFiles-haschanges`, false);
  const [data, _setData, removeData, dataRef] = useFastStorage(`ifms-referenceFiles-airport-list`, []);
  //const [data, _setData, removeData, dataRef] = useTypedFastStorage<Array<CodeAirport>>(`ifms-referenceFiles-airport-list`, []);
  //const [ei, setEi, , ediRef] = useFastStorage(`ifms-referenceFiles-airport-editindex`, -1);
  const [orderBy, setOrderBy] = useFastStorage(`ifms-referenceFiles-airport-orderby`, {
    orderBy: 'portName',
    orderByAscending: true,
    //general1: 'PortName',
    //pageSize: systemSettings.defaultPageSize,
  });

  const listSec = findSecurityTask(appContext,  FmsSecTask.CodeAirPort);

  const listItemType = 'CodeAirportListItem';
  //const { selectTableRow } = useTableNavigation('codes-airport-list', undefined, undefined, [], false, true, true);

  const [focusFirstCell, setFocusFirstCell] = useState(true);
  const { FilterButton, search } = useFilterBar(
    codesAirportList,
    undefined,
    getDefaultOrderBy, 
    String(componentIndex)
  );

  function handleSaveAndClose() {
    const saveBtn = document.getElementById('detail-save-button');
    if (saveBtn) {
      saveBtn.focus();
      const modals = document.querySelector('[class^="ReactModal__Content"]');
      if (!modals)
        saveBtn.click();
    }
  }
  
  function getDefaultOrderBy(): ListOrderBy {
    return {
      orderBy: 'code',
      orderByAscending: true,
    };
  }

  function setData(d: Array<CodeAirport>, callByFetchData: boolean = false) {
    setFocusFirstCell(callByFetchData);
    _setData(d, null, { forceClone: callByFetchData });
    setHasChanges(checkTabIsDirty(CodeAirportListItem, oriData.current, d));
  }

  async function fetchData(api: any, search: Search) {
    setLoading(true);

    const searchModel = CodeAirportListItem.calculateSearchParameters(search);
    const apiResult = await api.getListItems(JobNames.AirPort,
      listItemType,
      searchModel, 1, search.pageSize);

    setData(formatData(apiResult && apiResult.entities ? apiResult.entities : []), true);
    setOrderBy(getDefaultOrderBy());

    setFocusFirstCell(false);
    setLoading(false);
    setHasChanges(false);
  }

  useEffect(() => {
    if (isFilterObjectEmpty(search) && search.version === 1) reload(false);
    
    return () => {
      removeData();
    };
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useEffect(() => {
    if (loading || (isFilterObjectEmpty(search) && search.version === 1)) {
      return;
    }
    fetchData(appContext.api, search);
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search]
  );

  useEffect(() => {
    if (dataRef.current?.length > 0 && !loading) {
      oriData.current = _.cloneDeep(data);
      savedDataRef.current = _.cloneDeep(data);
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loading]
  );

  async function reload(clearAll:boolean = true) {
    const searchValue = clearAll ? initialSearch : search;
    await fetchData(appContext.api, searchValue);
  }

  function onHeaderClick(header: any) {
    if (header && header.id !== 'indicator') {
      if (header.id === orderBy.orderBy) {
        setOrderBy({ orderByAscending: !orderBy.orderByAscending });
      } else {
        setOrderBy({ orderBy: header.id, orderByAscending: true });
      }
    }

    removeData();
    sortingListData(CodeAirportListItem, data, setData, orderBy);
  }

  // async function requestOperationCancel() {
  //   const doCancel = await showGlobalYesNoDialog(<p>Are you sure you want to cancel Edit operation</p>);
  //   if (doCancel) {
  //     cancelOperation(false)
  //   } else {
  //     setTimeout(() => {
  //       selectTableRow(ediRef.current);
  //     }, 80);
  //   }
  // }

  // async function onRowIndexChanged(rowIndex:number) {
  //   if (rowIndex < 0 && ediRef.current >= 0) {
  //     setTimeout(() => {
  //       selectTableRow(ediRef.current);
  //     }, 80)
  //     return;
  //   }

  //   if (ediRef.current >= 0 && ediRef.current !== rowIndex) {
  //     await requestOperationCancel();
  //   }
  // }

  // function onRowSelect(rowIndex:number) {
  //   if (rowIndex >= 0 && rowIndex < dataRef.current.length && _.isEmpty(inlineElement) && (listSec?.grantEdit || listSec?.grantDel)) {
  //     setHasChanges(true);
  //     inlineElement = {...dataRef.current[rowIndex], isNew: false};
  //     setEi(rowIndex);
  //   }
  // }

  // function selectForNew() {
  //   inlineElement = {
  //     code: '', portName: '', usCode: '', isNew: true,
  //   };
  //   data.unshift(inlineElement);
  //   setData(data);
  //   setEi(0);
  //   setHasChanges(true);
  // }

  // function cancelOperation(leaveNew:boolean=false) {
  //   setHasChanges(false);

  //   clearTableSelection();
  //   setEi(-1);

  //   if (!leaveNew && inlineElement.isNew) {
  //     dataRef.current.splice(0, 1);
  //     setData(dataRef.current);
  //   }

  //   inlineElement = {};
  // }

  async function cancelOperation(){
    setHasChanges(false);
    removeData();
    return true;
  }

  function getIndexOfFirstInvalidCell(rowIndex: number){
    if(!data){
      return undefined;
    }

    if(!data[rowIndex].portName){
      return 1;
    }
    if (!data[rowIndex].code) {
      return 2;
    }
    if (allData) { //기존데이터 목록
      const newData = [...allData];
      if(newData.filter(x => x === data[rowIndex].code.toLowerCase()).length === 0) // 현재 목록에 없음
      {
        newData.splice(rowIndex, 1, data[rowIndex].code.toLowerCase());
        setAllData(newData);
        return undefined;
      }else{
        if(newData[rowIndex].toLowerCase() === data[rowIndex].code.toLowerCase()) return undefined;
        else{
          showGlobalOkDialog(<p>'{data[rowIndex].code}' is a duplicate value.</p>);
          return 2;
        }
      }
    } 
    else {
      const newData = [data[rowIndex].code.toLowerCase()];
      setAllData(newData);
    }
    return undefined;
  }
  
  async function duplicateCell(checkData: any) {
    let duplicateFieldText = '';

    let isDuplicate = false;
    let insList = checkData.filter((x: any) => x.isNew === true);
    let chgList = checkData.filter((x: any) => !x.isNew && x.hasBeenChanged === true);
    let focusRow = -1;

    if (!data) return isDuplicate;

    if (insList.length > 0 && data.length > 0) {
      for (let i = 0; i < insList.length; i++) {
        let count = 0;
        let tempRow = -1;
        for (let j = 0; j < data.length; j++) {
          if (insList[i].code.toUpperCase() === data[j].code.toUpperCase()) {
            if (count === 0) { tempRow = j; }
            count++;
            if (count > 1) {
              if (focusRow === -1) focusRow = tempRow;
              break;
            }
          }
        }
        if (count > 1) {
          duplicateFieldText += `'${insList[i].code}' is a duplicate value.\r\n`;
          isDuplicate = true;
        }
      }
    }

    if (chgList.length > 0 && data.length > 0) {
      for (let i = 0; i < chgList.length; i++) {
        let count = 0;
        let tempRow = -1;
        for (let j = 0; j < data.length; j++) {
          if (chgList[i].code.toUpperCase() === data[j].code.toUpperCase()) {
            if (data[j].hasBeenChanged === true && !data[j].isNew && tempRow === -1) { tempRow = j; }
            count++;
            if (count > 1) {
              if (focusRow === -1) focusRow = tempRow;
              break;
            }
          }
        }
        if (count > 1) {
          duplicateFieldText += `'${chgList[i].code}' is a duplicate value.\r\n`;
          isDuplicate = true;
        }
      }
    }

    if (!isDuplicate) {
      const apiResult = await appContext.api.checkDuplicatedKeys(JobNames.AirPort, listItemType, insList, chgList);
      if (apiResult && apiResult.success && apiResult.message) {
        duplicateFieldText = `${apiResult.message}`;
        isDuplicate = true;
      } else if (!apiResult || !apiResult.success) {
        await showGlobalOkDialog('Could not check duplication.\r\nPlease try later.');
        return true;
      }
    }
    else
      await showGlobalOkDialog(`${duplicateFieldText}Please try after correcting duplication.`);

    return isDuplicate;
  }

  // function addingLineToFirstLine() {
  //   addingToTheFirstLine.current = true;
  //   masterTableRef.current?.add();
  // }

  function addLineItem() {
    const newRow = new EditableCodeAirportInfoListItem();
    newRow.isNew = true;

    return {
      setDataFunc: (list: any) => setData(list),
      curListData: data,
      newData: newRow
    };
  }

  function applyToLineItem(rowIndex: number, change: Partial<EditableCodeAirportInfoListItem> | null) {
    if (!change || !data) {
      return;
    }
    const newData = [...data];
    const checkData = data[rowIndex];
    if (!change.isNew && (change.code !== checkData.code || change.usCode !== checkData.usCode || change.portName !== checkData.portName)) {
      _.merge(newData[rowIndex], { ..._.omit(change, ['isNew']), hasBeenChanged: true });
      setData(newData);
      //setHasChanges(true);
    }
  }

  function deleteEntity(index: number): boolean {
    if (!data) {
      return false;
    }


    const element = data[index];
    if (!element.isNew) {
      let deleteRow = savedDataRef.current.find(x => x.code === element.code);
      if (deleteRow) {
        deleteRow.isDelete = true;
      }
    }
    data.splice(index, 1);
    setData(data);

    const tempData = data.map((a: CodeAirportListItem) => a.code.toLowerCase());
    const newData = allData.splice(0,tempData.length,tempData);
    setAllData(newData);

    return true;
  }


  /**
  function checkValid() {
    if (data === null) return;

    let validData = dataRef.current.filter((x:any) => x.code);
    setData(validData);
  }
   */

  async function save() {
    // if (!inlineElement.isNew && !hasChanges) {
    //   cancelOperation();
    //   return;
    // }

    //<pt220817: portal
    // const apiResult = inlineElement.isNew
    //   ? await appContext.api.createCodesAirport(_.omit(inlineElement, ['isNew']))
    //   : await appContext.api.editCodesAirport({
    //     oldCode: data[ei].code,
    //     ..._.omit(inlineElement, ['isNew']),
    //   });

    // if (!apiResult || !apiResult.success) {
    //   return;
    // }

    // data[ei] = formatElement(apiResult.data);

    // const apiResult:ApiResult<InsertResult> = await appContext.api.saveListItem('CodeAirportListItem',
    //   inlineElement.isNew ? SQLType.Insert : SQLType.Update,
    //   _.omit(inlineElement, ['isNew']));
    // if(!apiResult.success) {
    //   await showGlobalOkDialog(<p>{apiResult.message}</p>);
    //   return;
    // }

    // // code is pkey
    // data[ei] = formatElement(inlineElement);
    // //pt220817>
    // cancelOperation(true);
    if (!data) {
      return;
    }
    
    // checkValid();
    let validData = dataRef.current.filter((x:any) => x.code);

    let errMsg = '';
    const dataToSave = validData.filter((x: any, i: number) => x.isNew || x.hasBeenChanged === true);
    const dataToDelete = savedDataRef.current.filter((x) => x.isDelete);
    if(dataToSave.length === 0 && dataToDelete.length === 0) {
      if (hasChanges) reload(false);
      return;
    }
    setLoading(true);

    for (let i = 0; i < dataToDelete.length; ++i) {
      const s = dataToDelete[i];
      const apiResult: ApiResult<InsertResult> = await appContext.api.saveListItem(listItemType, SQLType.Delete, _.omit(s, ['isDelete']));
      if (!apiResult || !apiResult.success)
        errMsg = apiResult?.message ?? constants.SAVE_ERROR_MESSAGE;
    }

    if (dataToSave.length > 0) {
      const isDuplicate = await duplicateCell(dataToSave);
      if(isDuplicate){
        setLoading(false);
        return;
      }
      const cidData = dataToSave.map((a: CodeAirportListItem) => a.cid?.toLowerCase()) as Array<String>;

      const secondSave = dataToSave.filter((x: CodeAirportListItem) => cidData.includes(x.code.toLowerCase()));
      const firstSave = dataToSave.filter((x: CodeAirportListItem) => !secondSave.includes(x));


      if (firstSave.length > 0) {
        const apiResult: ApiResult<InsertResult> = await appContext.api.saveList(listItemType, firstSave);
        if (!apiResult || !apiResult.success) {
          errMsg += (errMsg ? '\n' : '') + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE);
        }
        else {
          firstSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.code === x.code);
            if (index >= 0) {
              data[index].cid = x.code;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              x.isNew = false;
              //   delete data[index].isNew;
            }
          })
        }
      }
      if (secondSave.length > 0) {
        const apiResult: ApiResult<InsertResult> = await appContext.api.saveList(listItemType, secondSave);
        if (!apiResult || !apiResult.success) {
          errMsg += (errMsg ? '\n' : '') + (apiResult?.message ?? constants.SAVE_ERROR_MESSAGE);
        }
        else {
          secondSave.forEach((x: any) => {
            const index = data.findIndex((d: any) => d.code === x.code);
            if (index >= 0) {
              data[index].cid = x.code;
              data[index].hasBeenChanged = false;
            }
            if (x.isNew) {
              x.isNew = false;
            }
          })
        }
      }
    }

    if (errMsg) {
      await showGlobalOkDialog(<p>{errMsg}</p>);
      setLoading(false);
      return;
    }

    // setData(data);
    // savedDataRef.current = _.cloneDeep(data);
    // oriData.current = _.cloneDeep(data);

    // setLoading(false);
    // setHasChanges(false);
    reload(false);

    await showGlobalOkDialog(<p>The data has been saved</p>);
  }

  const tableOptions: ApplicableTableSettings = {
    customTableId: 'codes-airport-list',
    ref: tableRef,
    selectable: true,
    tableName: 'airportList',
    ...codesAirportList,
    height: '520px',
    //data: data.map((x:any, i:number) => ({...x, indicator: (i + 1).toString(), editIndex: ei, onDelete: deleteEntity, onSave: save, onCancel: cancelOperation, setHasChanges, requestOperationCancel, listSec})),
    data: (data ?? []).map((x: any, i: number) => ({ ...x, indicator: (i + 1).toString(), changeLineItem: applyToLineItem })),
    rowRenderers: [
      { accessor: 'indicator' },
      { accessor: 'portName', EditCell: PortNameRenderer },
      { accessor: 'code', EditCell: CodeRenderer },
      { accessor: 'usCode', EditCell: CustomCodeRenderer },
      // { accessor: 'action', Cell: ActionRenderer },
    ],
    getCellData: (accessor: any, rowData: any, rowIndex: number): CellData | undefined => {
       if (accessor === 'portName')
         return { type: TableCellTypes.Text, value: data[rowIndex].portName };
       else if (accessor === 'code')
        return { type: TableCellTypes.Text, value: data[rowIndex].code, align: TableCellAligns.Center };
       else if (accessor === 'usCode')
        return { type: TableCellTypes.Text, value: data[rowIndex].usCode, align: TableCellAligns.Center };
       else
         return undefined;
     },
     isCellLock: (accessor: string, rowData: any): boolean => {
      if(!(listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel))
        return true
      else
        return false;
     },
    orderBy: [orderBy],
    onHeaderClick,
    useEditControls: (listSec?.grantAdd || listSec?.grantEdit || listSec?.grantDel),
  };

  //const canSelectForNew = ei === -1;
  const canBeAdd = listSec?.grantAdd;

  return (
    <div style={{ marginTop: '40px' }}>
      {/* <Portal node={document && document.getElementById('settings-placeholder')}>
        <h1>Air <FontAwesomeIcon icon={faAngleRight} />&nbsp;Airport</h1>
        <div style={{width: '320px', fontWeight: 'bold'}}>
          <ClearableSearch initialValue={orderBy?.generalText1 || ''} onSearch={generalText1 => setOrderBy({general1: generalText1 ? orderBy?.orderBy : null, generalText1})} />
        </div>
      </Portal> */}
      
      {/* <div style={{ width: '320px', fontSize: '11px', fontWeight: 'bold' }}>
        <ClearableSearch
          initialValue={orderBy?.generalText1 || ''}
          onSearch={async generalText1 => {
            await reload(generalText1 === null ? '' : generalText1);
          }}
        />
      </div> */}
      <Portal node={document && document.getElementById('settings-command-placeholder')}>
        <div style={{display:'flex', marginTop:'5px'}}>
          <div className="ifms-common-bar-element">
            <GreenButton size="regular" disabled={loading} onClick={save}>Save</GreenButton>
          </div>
          <div className="ifms-common-bar-element ifms-left-separator-light-gray">
            <CloseButton name='settings-close' mode='light' onClick={refContext.cancel} />
          </div>
          {/* <div className="ifms-common-bar-element">
            <BlackInverseButton name='list-screen-refresh' size="large" style={{marginTop: '-5px'}} disabled={loading} onClick={reload} data-tooltip="Refresh" >
              <FontAwesomeIcon color='#47C83E' icon={faSync} />
            </BlackInverseButton>
          </div> */}
        </div>
      </Portal>

      <div style={{ display: 'flex', width: '554px' }}>
        <div style={{ marginLeft: 'auto' }}></div>
        <div style={{ position: 'relative', top: '-4px' }}>
          <div style={{ display: 'none' }}>
            <FilterButton /> &nbsp;&nbsp;
          </div>
          {//<SecondaryButton name='codes-airport-add-button' size="regular" disabled={!canBeAdd} onClick={() => { addingLineToFirstLine() }}><FontAwesomeIcon icon={faPlus} />&nbsp; Add Line</SecondaryButton> &nbsp; 
          }
          <BlackInverseButton name='list-screen-refresh' size="large" disabled={loading} onClick={() => { reload(false) }} data-tooltip="Refresh" >
            <FontAwesomeIcon color='#47C83E' icon={faSync} />
          </BlackInverseButton>
        </div>
      </div>

      <div style={{ width: '554px' }}>
        {/* <SmartTable {...tableOptions} /> */}
        <EditableTable
          initializeFocusedCell={focusFirstCell}
          ref={masterTableRef}
          tableOptions={tableOptions}
          deleteLine={deleteEntity}
          addLine={addLineItem}
          getIndexOfFirstInvalidCell={getIndexOfFirstInvalidCell}
        />
      </div>
    </div>
  );
}

export default Airports;
